import React, { useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Tooltip, Typography,
} from "@mui/material";
import { SearchRounded, RouterRounded, CircleRounded } from "@mui/icons-material";
import { green, red, grey } from "@mui/material/colors";
import api from "../../../../services/api";
import OltSelect from "../../../../components/OltSelect";
import CustomTextField from "../../../../components/CustomTextField";
import ProvisioningProgressModal from "../../../../components/ProvisioningProgressModal";
import { useNotify } from "../../../../components/Datatable/NotifyProvider";


function genTaskId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  return Math.random().toString(16).slice(2, 10);
}

// uma ONU é única por olt+placa+porta+id_from_olt
const rowKey = (r) => `${r.olt}-${r.board}-${r.port}-${r.id_from_olt}`;

function StatusDot({ signal }) {
  if (signal === 0) return <Tooltip title="Sem Sinal"><CircleRounded sx={{ color: red[500], fontSize: 14 }} /></Tooltip>;
  if (signal < 0) return <Tooltip title={`Online (${signal} dBm)`}><CircleRounded sx={{ color: green[500], fontSize: 14 }} /></Tooltip>;
  return <Tooltip title="Sem Coleta"><CircleRounded sx={{ color: grey[500], fontSize: 14 }} /></Tooltip>;
}


export default function RemBySn() {
  const { setNotify } = useNotify();
  const [olt, setOlt] = useState(null);            // OLT específica (opcional)
  const [sn, setSn] = useState("");
  const [loading, setLoading] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);  // busca ao vivo nas OLTs
  const [triedLive, setTriedLive] = useState(false);
  const [data, setData] = useState(null);          // todos os locais onde o SN está
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [taskId, setTaskId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [result, setResult] = useState(null);

  // se uma OLT está escolhida, mostra/marca só as linhas dela
  const visible = (data || []).filter(r => !olt || r.olt === olt);
  const allKeys = visible.map(rowKey);
  const allSelected = allKeys.length > 0 && allKeys.every(k => selected.has(k));
  const toggle = (k) => setSelected(prev => {
    const n = new Set(prev);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });
  const toggleAll = () => setSelected(prev => {
    const n = new Set(prev);
    if (allSelected) allKeys.forEach(k => n.delete(k));
    else allKeys.forEach(k => n.add(k));
    return n;
  });

  // escolher a OLT já marca todas as linhas dela (1 clique → remover)
  const handleOlt = (v) => {
    setOlt(v || null);
    if (v && data) setSelected(new Set(data.filter(r => r.olt === v).map(rowKey)));
    else setSelected(new Set());
  };

  const applyResults = (d) => {
    setData(d);
    if (olt) setSelected(new Set(d.filter(r => r.olt === olt).map(rowKey)));
  };

  const search = () => {
    if (sn.trim().length < 6) return;
    setLoading(true);
    setData(null);
    setSelected(new Set());
    setTriedLive(false);
    api.post("provisioning/olt/onu/find_by_sn", { sn: sn.trim() })
      .then(res => {
        const d = Array.isArray(res.data) ? res.data : [];
        applyResults(d);
        // OLT escolhida + nada no cadastro → checa AO VIVO nela automaticamente
        if (d.length === 0 && olt) searchLive();
      })
      .catch(e => setNotify({ open: true, severity: "error", duration: 6000, message: "Falha na busca: " + (e?.message || e) }))
      .finally(() => setLoading(false));
  };

  // fallback: varre as OLTs ao vivo (display ont info by-sn) — pra ONU órfã
  // (provisionada direto na OLT, fora do cadastro) / sanitização. É mais lento.
  const searchLive = () => {
    setLiveLoading(true);
    api.post("provisioning/olt/onu/find_by_sn_live", { sn: sn.trim(), olt: olt || null })
      .then(res => { applyResults(Array.isArray(res.data) ? res.data : []); setTriedLive(true); })
      .catch(e => setNotify({ open: true, severity: "error", duration: 6000, message: "Falha na busca ao vivo: " + (e?.message || e) }))
      .finally(() => setLiveLoading(false));
  };

  const remove = async () => {
    setConfirmOpen(false);
    const targets = (data || []).filter(r => selected.has(rowKey(r)));
    const tid = genTaskId();
    setTaskId(tid);
    setResult(null);
    setModalOpen(true);
    const results = await Promise.all(targets.map(t =>
      api.post(`provisioning/olt/rem/detail/?task_id=${tid}`, { olt: t.olt, board: t.board, port: t.port, sn: t.sn })
        .then(res => ({ t, ok: !res.data?.error && !res.data?.errors, msg: res.data?.error || "" }))
        .catch(e => ({ t, ok: false, msg: e?.message || String(e) }))
    ));
    const okCount = results.filter(r => r.ok).length;
    setResult({
      ok: okCount === results.length,
      message: `${okCount}/${results.length} remoção(ões) com sucesso`,
      info: results.map(r => ({
        label: `${r.t.olt_name} · 0/${r.t.board}/${r.t.port}`,
        value: r.ok ? "removido" : ("falha" + (r.msg ? ": " + r.msg : "")),
      })),
    });
    const removed = new Set(results.filter(r => r.ok).map(r => rowKey(r.t)));
    setData(prev => (prev || []).filter(r => !removed.has(rowKey(r))));
    setSelected(new Set());
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} alignItems="stretch">

      {/* OLT específica (opcional) + SN + Buscar — alinhado à esquerda */}
      <Box display="flex" gap={1} flexWrap="wrap" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}>
        <OltSelect
          size="small"
          value={olt}
          handler={handleOlt}
          admin={false}
          sx={{ minWidth: 240 }}
        />
        <CustomTextField
          size="small"
          label="SN do cliente"
          value={sn}
          onChange={e => setSn(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          sx={{ minWidth: 240 }}
        />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchRounded />}
          onClick={search}
          disabled={loading || sn.trim().length < 6}
        >
          Buscar
        </Button>
      </Box>

      {data && visible.length === 0 && (
        <Box display="flex" alignItems="center" gap={1}>
          {liveLoading && <CircularProgress size={16} />}
          <Typography variant="body2" color="text.secondary">
            {liveLoading
              ? "Não achei no cadastro — consultando a OLT…"
              : triedLive
                ? "Não encontrei esse SN na OLT."
                : olt
                  ? "Esse SN não está nessa OLT."
                  : "Nenhuma ONU com esse SN no cadastro. Selecione uma OLT pra checar ao vivo nela."}
          </Typography>
        </Box>
      )}

      {data && visible.length > 0 && (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.size > 0 && !allSelected}
                  onChange={toggleAll}
                />
              }
              label={`Selecionar todas (${visible.length})`}
            />
            <Typography variant="body2" color="text.secondary">{selected.size} selecionada(s)</Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            {visible.map(r => {
              const k = rowKey(r);
              return (
                <Box key={k} display="flex" alignItems="center" gap={1.5}
                  sx={{
                    border: 1,
                    borderColor: selected.has(k) ? "error.main" : "divider",
                    borderRadius: 1, p: 1,
                  }}>
                  <Checkbox checked={selected.has(k)} onChange={() => toggle(k)} />
                  <RouterRounded fontSize="small" sx={{ color: "text.secondary" }} />
                  <Box flexGrow={1} overflow="hidden">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={600} noWrap>{r.olt_name}</Typography>
                      <Chip size="small" variant="outlined" label={`0/${r.board}/${r.port}`} sx={{ fontFamily: "monospace" }} />
                      <StatusDot signal={r.signal} />
                      {r.live && <Chip size="small" color="warning" variant="outlined" label="fora do cadastro" />}
                    </Box>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary" noWrap>{r.description}</Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Button variant="contained" color="error" disabled={selected.size === 0}
            onClick={() => setConfirmOpen(true)} sx={{ alignSelf: "flex-start" }}>
            Remover selecionada(s)
          </Button>
        </>
      )}

      {/* confirmação */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar remoção</DialogTitle>
        <DialogContent>
          <Typography>
            Remover o SN <Box component="span" fontFamily="monospace">{sn}</Box> de <b>{selected.size}</b> local(is)?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Isso desprovisiona a ONU na(s) OLT(s) selecionada(s).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={remove}>Remover</Button>
        </DialogActions>
      </Dialog>

      <ProvisioningProgressModal
        open={modalOpen}
        taskId={taskId}
        mode="user"
        streamPath="/provisioning/stream"
        result={result}
        onClose={() => { setModalOpen(false); setResult(null); }}
      />
    </Box>
  );
}
